import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, type ReactElement } from 'react';
import { useDashboardStats, useHeatmap, useRustyPieces } from '@/lib/queries/insights';
import { StatTile } from '@/components/molecules/stat-tile';
import { PracticeHeatmap } from '@/components/practice-heatmap';
import { MasteryDistribution } from '@/components/mastery-distribution';
import { RustyPiecesList } from '@/components/rusty-pieces-list';
import { Card } from '@/components/atoms/card';
import { Micro } from '@/components/atoms/micro';
import { Segmented } from '@/components/atoms/segmented';

const HEATMAP_RANGE_DAYS = 90;
const HOURS_PER_MINUTE = 1 / 60;

type TimeRange = 'week' | 'month' | 'year';

const TIME_RANGE_OPTIONS: readonly { label: string; value: TimeRange }[] = [
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' },
] as const;

const TIME_RANGE_VALUES: readonly TimeRange[] = TIME_RANGE_OPTIONS.map((o) => o.value);
const DEFAULT_RANGE: TimeRange = 'month';

// Default-valued params are omitted from the URL: validateSearch returns
// undefined for a range at its default (or absent/garbage) so it does not
// serialize into the query string. The component supplies the default on read.
type HistorySearch = {
  range?: TimeRange;
};

export const Route = createFileRoute('/history')({
  component: HistoryPage,
  validateSearch: (raw: Record<string, unknown>): HistorySearch => ({
    range:
      TIME_RANGE_VALUES.includes(raw.range as TimeRange) && raw.range !== DEFAULT_RANGE
        ? (raw.range as TimeRange)
        : undefined,
  }),
});

function SectionHeading({ label }: { label: string }): ReactElement {
  return (
    <Micro className="mb-4 block">{label}</Micro>
  );
}

function HistoryPage(): ReactElement {
  const { range } = Route.useSearch();
  const timeRange = range ?? DEFAULT_RANGE;
  const navigate = useNavigate({ from: '/history' });

  const setTimeRange = (next: TimeRange): void => {
    void navigate({ search: (prev) => ({ ...prev, range: next }) });
  };

  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: rustyPieces } = useRustyPieces();

  const heatmapRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - HEATMAP_RANGE_DAYS);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  }, []);

  const { data: heatmapData } = useHeatmap(heatmapRange.start, heatmapRange.end);

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--ink)]">
      <header className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-6 pt-10 pb-8 lg:px-12 lg:pt-14 lg:pb-10">
        <div className="mx-auto max-w-5xl flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Micro className="mb-1">History</Micro>
            <h1 className="font-display text-3xl font-semibold tracking-tight lg:text-4xl">Practice history</h1>
            <p className="mt-2 text-sm text-[var(--ink-3)]">
              Streaks, activity, and the pieces most likely to go rusty.
            </p>
          </div>
          <Segmented
            value={timeRange}
            onChange={setTimeRange}
            options={TIME_RANGE_OPTIONS}
            size="sm"
          />
        </div>
      </header>

      <main className="px-6 pt-8 pb-24 lg:px-12">
        <div className="mx-auto max-w-5xl space-y-10">
          {statsLoading && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)]"
                />
              ))}
            </div>
          )}

          {stats && (
            <>
              <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile
                  label="Streak"
                  value={String(stats.streakDays)}
                  icon="flame"
                  tone="warm"
                  unit={stats.streakDays === 1 ? 'day' : 'days'}
                />
                <StatTile
                  label="Sessions"
                  value={String(stats.sessionsLast7Days)}
                  tone="brand"
                  unit="last 7 days"
                />
                <StatTile
                  label="In repertoire"
                  value={String(stats.totalRepertoirePieces)}
                  icon="book"
                  tone="neutral"
                  unit="tracked"
                />
                <StatTile
                  label="Avg duration"
                  value={`${Math.round(stats.averageSessionDurationMinutes)}m`}
                  tone="brand"
                  delta={`${(stats.averageSessionDurationMinutes * HOURS_PER_MINUTE).toFixed(1)}h avg`}
                />
              </section>

              <section>
                <SectionHeading label="Mastery distribution" />
                <Card padding="lg">
                  <MasteryDistribution breakdown={stats.masteryBreakdown} />
                </Card>
              </section>
            </>
          )}

          <section>
            <SectionHeading label="Practice activity" />
            <Card padding="lg">
              {heatmapData ? (
                <PracticeHeatmap
                  startDate={heatmapData.startDate}
                  endDate={heatmapData.endDate}
                  activity={heatmapData.activity}
                />
              ) : (
                <div className="h-32 animate-pulse rounded-[var(--r-sm)] bg-[var(--bg-soft)]" />
              )}
            </Card>
          </section>

          <section>
            <SectionHeading label="Going rusty" />
            <Card padding="lg">
              <RustyPiecesList pieces={rustyPieces ?? []} />
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
}
